import {Component, computed, effect, Inject, inject, Signal, signal} from '@angular/core';
import {MenuItem, PrimeTemplate} from "primeng/api";
import {DocumentationType} from "../../models/documentation.model";
import {DocumentationService} from "../../service/documentation.service";
import {AuthService} from "../../service/auth.service";
import {Menu} from "primeng/menu";
import {Avatar} from "primeng/avatar";
import {ToggleSwitch} from "primeng/toggleswitch";
import {Divider} from "primeng/divider";
import {Chip} from "primeng/chip";
import {Button, ButtonDirective} from "primeng/button";
import {selectTenantRoleData} from "../../state/auth/auth.selectors";
import {Store} from "@ngrx/store";
import {AppState} from "../../state/app.state";
import {MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService} from "@azure/msal-angular";
import {Router} from "@angular/router";
import {Popover} from "primeng/popover";
import {Menubar} from "primeng/menubar";
import { NgClass, NgOptimizedImage, NgStyle } from "@angular/common";
import {InteractionStatus, RedirectRequest} from "@azure/msal-browser";
import {environment} from "../../../../environments/environment";
import {Subject} from "rxjs";
import {filter, takeUntil} from "rxjs/operators";
import {getUsersRoleAndTenantData} from "../../state/auth/auth.action";
import {FormsModule} from "@angular/forms";
import {Ripple} from "primeng/ripple";
import {BsThemeService} from "../../service/bs-theme.service";
import {toSignal} from "@angular/core/rxjs-interop";
import {BsTheme} from '../../models/bs-theme.model';
import {SideNavigationService, SideNavigationState} from "../../service/side-navigation.service";
import {BsSwLogoComponent} from "../../icons/bs-sw-logo.component";
import {BsSwLogoTextComponent} from "../../icons/bs-sw-logo-text.component";
import {IconField} from "primeng/iconfield";
import {InputIcon} from "primeng/inputicon";
import {InputText} from "primeng/inputtext";
import {Breadcrumb} from "primeng/breadcrumb";
import {BsGlobalSearchBarComponent} from "../../bs-global-search-bar/bs-global-search-bar.component";
import {BreadCrumbService} from "../../services/bread-crumb.service";

@Component({
  selector: 'bs-top-navigation',
  imports: [
    Menu,
    Avatar,
    ToggleSwitch,
    Divider,
    Button,
    Chip,
    Popover,
    Menubar,
    NgClass,
    FormsModule,
    PrimeTemplate,
    ButtonDirective,
    Ripple,
    NgOptimizedImage,
    BsSwLogoComponent,
    BsSwLogoTextComponent,
    NgStyle,
    IconField,
    InputIcon,
    InputText,
    Breadcrumb,
    BsGlobalSearchBarComponent
],
  templateUrl: './bs-top-navigation.component.html',
  styleUrl: './bs-top-navigation.component.css',
  standalone: true
})
export class BsTopNavigationComponent {
  documentationService = inject(DocumentationService);
  bsAuthService = inject(AuthService);
  store = inject(Store<AppState>);
  broadcastService = inject(MsalBroadcastService);
  authService = inject(MsalService);
  router = inject(Router);
  themeService = inject(BsThemeService);
  title = 'Steelworks';
  loginDisplay = false;
  customerName = '';
  customerId = '';
  roleId = signal<string>('');
  customerEmail = '';
  showCuiBanner = signal(false);
  tenantRoleData = this.store.selectSignal(selectTenantRoleData);
  webappAccess = computed(() => !!this.tenantRoleData()?.tenant?.webappAccess);
  theme = toSignal(this.themeService.theme$);
  darkModeChecked = computed(() => {
    return this.theme() === BsTheme.DARK;
  });

  logoUrl = computed(() => {
    return this.darkModeChecked() ? '../SteelWorksDarkModeLarge.png' : '../SteelWorksLightModeLarge.png';
  });

  private readonly destroying$ = new Subject<void>();

  menuBarStyleTheme = {
    padding: '0',
    margin: '0',
  }
  private addinFileName = signal('Steelworks Add-in.msi');
  private userManualFileName = signal<string | undefined>(undefined);
  documentationItems: MenuItem[] = [
    {
      label: 'General',
      items: [
        {
          label: 'User Manual',
          icon: 'pi pi-file-pdf',
          command: () => this.documentationService.download(DocumentationType.UserManual, this.userManualFileName())
        },
        {
          label: 'Cut Face & Case',
          icon: 'pi pi-file-pdf',
          command: () => this.documentationService.download(DocumentationType.CutFaceCutCase)
        },
        {
          label: 'Empty CMX',
          icon: 'pi pi-file-excel',
          command: () => this.documentationService.download(DocumentationType.CmxEmpty)
        },
      ]
    },
    { separator: true },
    {
      label: 'Solidworks',
      items: [
        {
          label: 'CMX Table Template',
          icon: 'pi pi-file',
          command: () => this.documentationService.download(DocumentationType.CmxTableTemplate)
        },
        {
          label: 'Steelworks Add-In',
          icon: 'pi pi-file',
          command: () => this.documentationService.download(DocumentationType.SteelworksAddIn, this.addinFileName())
        },
        {
          label: 'Steelworks Macro',
          icon: 'pi pi-file',
          command: () => this.documentationService.download(DocumentationType.SteelworksMacro)
        },
        {
          label: 'Example CMX',
          icon: 'pi pi-file-excel',
          command: () => this.documentationService.download(DocumentationType.CmxSolidworksExample)
        },
      ]
    },
    { separator: true },
    {
      label: 'Tekla',
      items: [
        {
          label: 'Custom Properties',
          icon: 'pi pi-file',
          command: () => this.documentationService.download(DocumentationType.TeklaCustomProperties)
        },
        {
          label: 'Report Template',
          icon: 'pi pi-file',
          command: () => this.documentationService.download(DocumentationType.TeklaReportTemplate)
        },
        {
          label: 'Example CMX',
          icon: 'pi pi-file-excel',
          command: () => this.documentationService.download(DocumentationType.CmxTeklaExample)
        },
      ]
    }
  ];

  items = signal([
    {
      label: 'Projects',
      routerLink: 'projects'
    },
    {
      label: 'CAD Data',
      routerLink: 'cad-data'
    },
    {
      label: 'Common Drawings',
      routerLink: 'drawing',
    },
    {
      label: 'Build Kits',
      routerLink: 'build-kit'
    },
    {
      label: 'Reports',
      routerLink: 'reports'
    },
    {
      label: 'Library',
      items: [
        {
          label: 'Material',
          items:
            [
              {
                label: 'Alloy',
                routerLink: 'library/material-alloy'
              },
              {
                label: 'Condition',
                routerLink: 'library/material-condition'
              },
              {
                label: 'Family',
                routerLink: 'library/material-family'
              },
              {
                label: 'Grade',
                routerLink: 'library/material-grade'
              },
              {
                label: 'Process',
                routerLink: 'library/material-process'
              },
              {
                label: 'Specification',
                routerLink: 'library/material-specification'
              },
              {
                label: 'Temper',
                routerLink: 'library/material-temper'
              },
              {
                label: 'Type',
                routerLink: 'library/material-type'
              },
            ]
        },
        {
          label: 'Stock Size',
          items: [
            {
              label: 'Shape Type',
              routerLink: 'library/stock-size-shape-type'
            },
            {
              label: 'Shape Type Material Family',
              routerLink: 'library/stock-size-shape-type-material-family'
            },
            {
              label: 'Shape Type Material Alloy',
              routerLink: 'library/stock-size-shape-type-material'
            },
          ]
        },
        {
          label: 'COTS',
          items: [
            {
              label: 'Type',
              routerLink: 'library/cots-type'
            },
            {
              label: 'Sub Type',
              routerLink: 'library/cots-sub-type'
            },
          ]
        },
        {
          label: 'Shape',
          items: [
            {
              label: 'Type',
              routerLink: 'library/shape-type'
            },
            {
              label: 'Custom',
              routerLink: 'library/custom-shape'

            },
          ]
        },
        {
          label: 'Organization',
          routerLink: 'library/organization'

        },
        {
          label: 'Standard',
          routerLink: 'library/standard'
        },
        {
          label: 'Process',
          routerLink: 'library/process'
        },
        {
          label: 'Traveler Category',
          routerLink: 'library/traveler-category'
        },

      ]
    },
  ]);

  constructor(@Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration) {
  }

  toggleDarkMode() {
    const element = document.querySelector('html');
    element?.classList.toggle('my-app-dark');
    const isDarkMode = document.querySelector('html.my-app-dark');
    if (isDarkMode){
      localStorage.setItem("darkModeSelected", 'true');
      this.themeService.setTheme(BsTheme.DARK);
    } else {
      localStorage.setItem("darkModeSelected", 'false');
      this.themeService.setTheme(BsTheme.LIGHT);
    }
  }

  ngOnInit() {
    this.documentationService.getLatestInstaller()
      .pipe(takeUntil(this.destroying$))
      .subscribe(installer => {
        if (installer?.version) {
          this.addinFileName.set(`Steelworks Add-in v${installer.version}.msi`);
        }
      });
    this.documentationService.getLatestUserManual()
      .pipe(takeUntil(this.destroying$))
      .subscribe(manual => {
        if (manual?.fileName) {
          this.userManualFileName.set(manual.fileName);
        }
      });
    this.bsAuthService.customerClaimReplay
      .pipe(takeUntil(this.destroying$))
      .subscribe((claims: any) => {
        this.customerName = claims?.name ?? '';
        this.customerEmail = claims?.email ?? '';
        this.customerId = claims?.customerId ?? '';
        this.roleId.set(claims?.roleId ?? '');

        this.store.dispatch(getUsersRoleAndTenantData({
          tenantId: this.customerId,
          roleId: this.roleId(),
        }));
      });

    this.broadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
      })
  }

  login() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  logout() {
    this.authService.logoutRedirect({
      postLogoutRedirectUri: environment.config.auth.redirectUri
    });
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  ngOnDestroy(): void {
    this.destroying$.next(undefined);
    this.destroying$.complete();
  }
}
